import { Inject, Injectable, Optional } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Salesperson } from '@galaxy/types';
import { SwitchingWorkState } from '@vendasta/rx-utils/work-state';
import {
  DateRangeFilter,
  ListOpportunitiesRequestFilters,
  ListOpportunitiesRequestSortField,
  ListOpportunitiesRequestSortOptionInterface,
  Opportunity,
  OpportunityRevenuePeriod,
  SalesOpportunitiesService,
} from '@vendasta/sales-opportunities';
import { DateRange } from '@vendasta/uikit';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, firstValueFrom, merge } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { PipelineFiltersService, PipelineTableFilters } from '../../filters/filters-service/pipeline-filters.service';
import { PipelineField, PipelineSort, SortDirection, buildSortOptions, defaultSortOption } from '../pipeline-sort';
import { PipelineStatuses } from '../pipeline-status-checkbox/pipeline-status-checkbox.service';
import { PipelineUIFilters } from '../pipeline/pipeline-service.interface';
import { PotentialRevenueService } from '../pipeline/potential-revenue/potential-revenue.service';
import { SearchbarService } from '../searchbar/searchbar.service';
import { MARKET_ID_TOKEN, PARTNER_ID_TOKEN } from '../tokens';
import { OpportunityRow, PipelineTableServiceInterface } from './pipeline-table.service.interface';
import {
  ACCOUNT_DETAILS_URL_TOKEN,
  ALL_SALESPEOPLE_TOKEN,
  DEFAULT_CURRENCY_TOKEN,
  OPPORTUNITY_DETAILS_URL_TOKEN,
} from './tokens';

const DEFAULT_SEARCH_TERM = '';

const DIRECTION_PROPERTY = 'direction';

enum queryConstants {
  SORT_DIRECTION = 'sortDir',
  ACTIVE_COLUMN = 'sortCol',
  SEARCH_TERM = 'searchTerm',
}

export interface SearchRequest {
  filters: ListOpportunitiesRequestFilters;
  partnerId: string;
  marketId: string;
  sort: ListOpportunitiesRequestSortOptionInterface;
  searchTerm: string;
}

interface InternalSearchRequest extends SearchRequest {
  cursor: string;
  reset: boolean;
}

interface OpportunitiesSearchResponse {
  rows: OpportunityRow[];
  reset: boolean;
}

export const PAGE_SIZE = 20;

export const calculateRevenueInMonth = (
  initialRevenue: number,
  recurringRevenue: number,
  oppCloseDate: Date,
  filterDate: Date,
) => {
  const safeInitialRevenue = initialRevenue || 0;
  const safeRecurringRevenue = recurringRevenue || 0;

  if (!filterDate) {
    return 0;
  }
  if (oppCloseDate.getMonth() === filterDate.getMonth() && oppCloseDate.getFullYear() === filterDate.getFullYear()) {
    return safeInitialRevenue + safeRecurringRevenue;
  }
  return safeRecurringRevenue;
};

function pipelineStatusesToListOpportunitiesRequestFilter(statuses: PipelineStatuses): string[] {
  const out = [];
  if (statuses?.open) {
    out.push('open');
  }
  if (statuses?.won) {
    out.push('closed-won');
  }
  if (statuses?.lost) {
    out.push('closed-lost');
  }
  return out;
}

@Injectable()
export class PipelineTableService implements PipelineTableServiceInterface {
  readonly selectedFilters$: Observable<PipelineTableFilters>;
  readonly resetToFirstPage$: Observable<number>;

  readonly opportunities$: Observable<OpportunityRow[]>;

  private readonly totalItems$$ = new ReplaySubject<number>(1);
  readonly totalItems$ = this.totalItems$$.asObservable().pipe(startWith(0));

  private readonly nextCursor$$ = new ReplaySubject<string>(1);
  // WARNING: do not use outside this class, only public for tests
  // TODO (SSC-3893): make private
  readonly nextCursor$ = this.nextCursor$$.asObservable();

  private readonly triggerCursorUpdate$$ = new ReplaySubject<void>(1);
  private readonly triggerCursorUpdate$ = this.triggerCursorUpdate$$.pipe(startWith({}));

  private readonly triggerTableReload$$ = new Subject<void>();
  readonly triggerTableReload$ = this.triggerTableReload$$.asObservable().pipe(startWith({}));

  private readonly searchTerm$: Observable<string>;
  private readonly lastSearchTerm$$ = new BehaviorSubject<string>(DEFAULT_SEARCH_TERM);
  readonly lastSearchTerm$ = this.lastSearchTerm$$.asObservable();

  private readonly sort$$ = new ReplaySubject<PipelineSort>(1);
  readonly sort$: Observable<PipelineSort>;
  private lastSort$$ = new BehaviorSubject<PipelineSort>({} as PipelineSort);

  private lastMarketId$$ = new BehaviorSubject<string>('');

  private readonly enabledColumns$$ = new ReplaySubject<string[]>(1);
  readonly enabledColumns$ = this.enabledColumns$$.asObservable();

  private readonly isMonthlyFilterSet$ = this.potentialRevenueService.selectedDate$.pipe(
    startWith(false),
    map((selectedDate) => !!selectedDate),
  );

  private readonly listOpportunitiesState = new SwitchingWorkState<InternalSearchRequest, OpportunitiesSearchResponse>(
    (e) => this.searchOpportunities(e),
  );

  readonly loading$ = this.listOpportunitiesState.isLoading$.pipe(shareReplay(1));
  readonly listOpportunityError$ = this.listOpportunitiesState.isSuccess$.pipe(map((s) => !s));

  readonly preferredCurrency$: Observable<string> = this.defaultCurrency$;

  // WARNING: do not use outside this class, only public for tests
  // TODO (SSC-3893): have only in a constant in the constructor again
  readonly listOpportunityTrigger$: Observable<InternalSearchRequest>;

  selectedUIFilters$ = this.pipelineFiltersService.selectedUIFilters$;

  constructor(
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID_TOKEN) private readonly marketId$: Observable<string>,
    @Inject(ALL_SALESPEOPLE_TOKEN) private readonly allSalespeople$: Observable<Salesperson[]>,
    @Inject(DEFAULT_CURRENCY_TOKEN) private readonly defaultCurrency$: Observable<string>,
    @Inject(OPPORTUNITY_DETAILS_URL_TOKEN)
    @Optional()
    private readonly generateOpportunityDetailsUrl: (opp: Opportunity) => string,
    @Inject(ACCOUNT_DETAILS_URL_TOKEN) private readonly generateAccountDetailsUrl: (opp: Opportunity) => string,
    private readonly salesOpportunitiesService: SalesOpportunitiesService,
    private readonly searchbarStore: SearchbarService,
    private readonly potentialRevenueService: PotentialRevenueService,
    private readonly pipelineFiltersService: PipelineFiltersService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {
    this.selectedFilters$ = this.pipelineFiltersService.selectedFiltersListRequestData$.pipe(
      tap(() => this.nextCursor$$.next(null)),
    );

    this.searchTerm$ = this.searchbarStore.searchTerm$.pipe(debounceTime(400), startWith(DEFAULT_SEARCH_TERM));

    const routeParams = this.activatedRoute?.snapshot?.queryParams;
    this.sort$ = this.setInitialSort(routeParams);
    this.setInitialSearchTerm(routeParams);
    this.setupLiveQueryParams();

    this.resetToFirstPage$ = combineLatest([this.searchTerm$, this.selectedFilters$, this.sort$]).pipe(map(() => 0));

    this.opportunities$ = this.getOpportunities$();

    this.listOpportunityTrigger$ = combineLatest([
      this.triggerTableReload$,
      this.marketId$,
      this.selectedFilters$,
      this.searchTerm$,
      this.triggerCursorUpdate$,
      this.sort$,
    ]).pipe(
      switchMap(([triggerReload, marketId, filters, searchTerm, triggerCursor, sort]) => {
        return combineLatest([this.partnerId$, this.nextCursor$]).pipe(
          take(1),
          map(([partnerId, cursor]) => {
            return [triggerReload, filters, partnerId, marketId, searchTerm, cursor, triggerCursor, sort];
          }),
        );
      }),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      map(
        ([, filters, partnerId, marketId, searchTerm, cursor, , sort]: [
          void,
          PipelineTableFilters,
          string,
          string,
          string,
          string,
          void,
          PipelineSort,
        ]) => {
          let shouldGoToFirstPage = false;
          const lastSortValue = this.lastSort$$.getValue();
          const hasSortChanged =
            lastSortValue?.isAscending !== sort.isAscending || lastSortValue?.active !== sort.active;
          if (
            this.cursorIsEmpty(cursor) ||
            this.searchChanged(searchTerm) ||
            hasSortChanged ||
            this.marketIdChanged(marketId)
          ) {
            cursor = null;
            shouldGoToFirstPage = true;
          }

          this.lastSort$$.next(sort);
          this.lastSearchTerm$$.next(searchTerm);
          this.lastMarketId$$.next(marketId);

          return <InternalSearchRequest>{
            filters: this.getUpdatedFilters(filters),
            partnerId: partnerId,
            marketId: marketId,
            sort: buildSortOptions(sort),
            searchTerm: searchTerm,
            cursor: cursor,
            reset: shouldGoToFirstPage,
          };
        },
      ),
    );

    this.listOpportunityTrigger$.subscribe((req) => this.listOpportunitiesState.startWork(req));
  }

  applyFiltersFromQueryParams(): void {
    this.pipelineFiltersService.applyFiltersFromQueryParams();
  }

  applySavedFiltersFromStorage(searchTerm: string, filters: PipelineUIFilters, sort: PipelineSort | Sort): void {
    this.searchbarStore.setSearchTerm(searchTerm);
    this.pipelineFiltersService.applySavedFiltersFromStorage(filters);
    if (DIRECTION_PROPERTY in sort) {
      const legacySort = <PipelineSort>{
        active: sort.active as PipelineField,
        isAscending: sort.direction === SortDirection.ASC,
      };
      this.applySorting(legacySort);
    } else {
      this.applySorting(sort as PipelineSort);
    }
  }

  getOpportunities$(): Observable<OpportunityRow[]> {
    return this.listOpportunitiesState.workResults$.pipe(
      scan(
        (accumulator, response) => {
          if (response.reset === true) {
            return response.rows;
          }
          return accumulator.concat(response.rows);
        },
        <OpportunityRow[]>[],
      ),
      shareReplay(1),
    );
  }

  reloadTable(): void {
    this.triggerTableReload$$.next();
  }

  applySorting(sort: PipelineSort): void {
    this.sort$$.next(sort);
  }

  async getNextPage(): Promise<void> {
    const next = await firstValueFrom(this.nextCursor$);
    if (next !== '' && !!next) {
      this.triggerCursorUpdate$$.next();
    }
    return;
  }

  setEnabledColumns$(): Observable<void> {
    const alwaysVisibleColumns = [
      PipelineField.OPPORTUNITY,
      PipelineField.ACCOUNT,
      PipelineField.PIPELINE_NAME,
      PipelineField.PIPELINE_STAGE,
      PipelineField.POTENTIAL,
      PipelineField.TYPE,
      PipelineField.STATUS,
      PipelineField.MEDDIC,
      PipelineField.CREATED,
      PipelineField.EXPECTED_CLOSE,
      PipelineField.ACTUAL_CLOSE,
      PipelineField.ASSIGNEE,
      PipelineField.ASSIGNED_DATE,
      PipelineField.CALLS,
      PipelineField.EMAILS,
      PipelineField.MEETINGS,
      PipelineField.TOTAL_EFFORTS,
      PipelineField.CONNECTIONS,
      PipelineField.LAST_ACTIVITY,
      PipelineField.LAST_CONNECTED,
    ];

    return this.isMonthlyFilterSet$.pipe(
      map((isMonthlyColumnSet) => {
        const defaultColumns = Object.assign([], alwaysVisibleColumns);

        if (isMonthlyColumnSet) {
          const potentialColumnPosition = defaultColumns.indexOf(PipelineField.POTENTIAL);
          defaultColumns.splice(potentialColumnPosition + 1, 0, PipelineField.MONTHLY_REVENUE);
        }

        this.enabledColumns$$.next(defaultColumns);
      }),
    );
  }

  private cursorIsEmpty(cursor: string): boolean {
    return !cursor;
  }

  private searchChanged(term: string): boolean {
    const lastSearchTerm = this.lastSearchTerm$$.getValue();
    return term !== lastSearchTerm;
  }

  private marketIdChanged(marketId: string): boolean {
    const lastMarketId = this.lastMarketId$$.getValue();
    return !!lastMarketId && lastMarketId != marketId;
  }

  private getUpdatedFilters(filters: PipelineTableFilters): ListOpportunitiesRequestFilters {
    const revDate = filters.monthlyRevenueDate
      ? { revenueDate: filters.monthlyRevenueDate, revenuePeriod: OpportunityRevenuePeriod.SELECTED_MONTH }
      : null;
    const statuses = pipelineStatusesToListOpportunitiesRequestFilter(filters.statuses);

    const tags = [];
    if (filters?.oppTags?.length > 0) {
      tags.push(...filters.oppTags);
    }
    if (filters?.acctTags?.length > 0) {
      tags.push(...filters.acctTags);
    }

    let probabilityRange = null;
    if (filters?.pipelines?.length === 1 && !!filters.pipelineStage) {
      probabilityRange = filters.pipelines[0].findProbabilityRangeFromStage(filters.pipelineStage);
    }

    return <ListOpportunitiesRequestFilters>{
      accountGroupId: filters.accountGroupId !== null ? filters.accountGroupId : null,
      salesPersonIds: filters.salespeopleIds?.length > 0 ? filters.salespeopleIds : null,
      pipelineIds: filters.pipelines ? filters.pipelines.map((p) => p.id) : null,
      tags: tags.length > 0 ? tags : null,
      lineItemProductIds: filters.products?.length > 0 ? filters.products.map((p) => p.productId) : null,
      packageIds: filters.packages?.length > 0 ? filters.packages.map((p) => p.packageId) : null,
      actualCloseDateFilter: this.dateRangeToDateRangeFilter(filters.actualClose),
      expectedCloseDateFilter: this.dateRangeToDateRangeFilter(filters.expectedClose),
      createdDateFilter: this.dateRangeToDateRangeFilter(filters.createdDate),
      probabilityRangeFilter: probabilityRange,
      revenuePeriodFilter: revDate,
      pipelineStages: statuses,
      assignedDate: this.dateRangeToDateRangeFilter(filters.assignedDate),
      opportunityTypes: filters.types?.length > 0 ? filters.types.map((t) => t.id) : null,
    };
  }

  private dateRangeToDateRangeFilter(dateRange: DateRange): DateRangeFilter {
    return dateRange ? <DateRangeFilter>{ beginRange: dateRange.start, endRange: dateRange.end } : null;
  }

  private search(req: InternalSearchRequest): Observable<OpportunitiesSearchResponse> {
    return combineLatest([
      this.salesOpportunitiesService.list(
        req.partnerId,
        req.marketId,
        req.filters,
        req.sort,
        req.searchTerm,
        PAGE_SIZE,
        req.cursor,
      ),
      this.allSalespeople$,
    ]).pipe(
      map(([response, salespeople]) => {
        this.totalItems$$.next(response.totalResults);
        this.nextCursor$$.next(response.nextCursor);
        this.potentialRevenueService.setMonthlyRevenue(response.totalRevenueForRevenuePeriod || 0);
        this.potentialRevenueService.setPotentialRevenue(response.totalProjectedFirstYearValue || 0);
        const rows = this.rawOpportunitiesToRows(
          response.results || [],
          salespeople || [],
          req.filters?.revenuePeriodFilter?.revenueDate,
        );
        return <OpportunitiesSearchResponse>{
          rows: rows,
          reset: req.reset,
        };
      }),
    );
  }

  private rawOpportunitiesToRows(
    rawOpps: Opportunity[],
    salespeople: Salesperson[],
    revenueMonth?: Date,
  ): OpportunityRow[] {
    return rawOpps.map((opp) => {
      return <OpportunityRow>{
        id: opp.opportunityId,
        name: opp.name,
        opportunityUrl: this.generateOpportunityDetailsUrl ? this.generateOpportunityDetailsUrl(opp) : null,
        accountUrl: this.generateAccountDetailsUrl(opp),
        accountGroupId: opp.accountGroupId,

        pipelineId: opp.pipelineId,
        probability: opp.probability,

        potentialRevenue: opp.projectedFirstYearValue,
        monthlyRevenue: calculateRevenueInMonth(
          opp?.forecasting?.revenueInMonth?.initialRevenue,
          opp?.forecasting?.revenueInMonth?.recurringRevenue,
          opp.expectedCloseDate,
          revenueMonth,
        ),
        status: opp.pipelineStage,
        score:
          opp.scoring?.scores.reduce((total, score) => {
            const currentScore = score?.scoreValue || 0;
            return total + currentScore;
          }, 0) ?? 0,

        createdDate: opp.created,
        expectedCloseDate: opp.expectedCloseDate,
        actualCloseDate: opp.actualClosedDate?.getFullYear() ? opp.actualClosedDate : null,
        lastActivityDate: opp.lastActivityDate?.getFullYear() ? opp.lastActivityDate : null,
        lastConnectedDate: opp.lastConnectedDate?.getFullYear() ? opp.lastConnectedDate : null,
        assignedDate: opp.assignedDate?.getFullYear() ? opp.assignedDate : null,

        assigneeName: salespeople.length > 0 ? salespeople.find((sp) => sp.id === opp.salesPersonId)?.fullName : '',
        calls: -1,
        emails: -1,
        meetings: -1,
        totalEfforts: -1,
        connections: -1,

        type: opp.opportunityType,
      };
    });
  }

  private searchOpportunities(req: InternalSearchRequest): Observable<OpportunitiesSearchResponse> {
    return this.search(req);
  }

  private setupLiveQueryParams(): void {
    merge(
      this.sort$.pipe(
        map(
          (sort) =>
            <Params>{
              [queryConstants.SORT_DIRECTION]: JSON.stringify(sort?.isAscending),
              [queryConstants.ACTIVE_COLUMN]: JSON.stringify(sort?.active, (_, v) => v || undefined),
            },
        ),
      ),
      // This is debounced by the searchTerm observable already
      this.searchTerm$.pipe(
        map(
          (st) =>
            <Params>{
              [queryConstants.SEARCH_TERM]: st || undefined,
            },
        ),
      ),
    ).subscribe((params: Params) => {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: params,
        queryParamsHandling: 'merge',
      });
    });
  }

  private setInitialSort(routeParams: Params): Observable<PipelineSort> {
    const directionFromUrl: boolean = JSON.parse(routeParams?.[queryConstants.SORT_DIRECTION] || null);
    const columnFromUrl: PipelineField = JSON.parse(
      routeParams?.[queryConstants.ACTIVE_COLUMN] || ListOpportunitiesRequestSortField.PROJECTED_FIRST_YEAR_VALUE,
    );

    this.sort$$.next(null);

    if (directionFromUrl || columnFromUrl) {
      this.sort$$.next({ isAscending: directionFromUrl, active: columnFromUrl });
    } else {
      this.sort$$.next(defaultSortOption);
    }

    return this.sort$$.asObservable();
  }

  private setInitialSearchTerm(routeParams: Params): void {
    const searchTermFromUrl: string = routeParams?.[queryConstants.SEARCH_TERM] || '';
    this.searchbarStore.setSearchTerm(searchTermFromUrl);
  }
}
